import {useCallback, useRef, useState} from "react";
import {loadProgress, saveProgress, loadHistoryIndex, saveHistoryIndex} from "../utils/storage";

export default function useProgress({ slug, video, categoryVideos }) {
  const vimeoPlayerRef = useRef(null);
  const lastSavedSecondRef = useRef(-1);
  const [progressMap, setProgressMap] = useState({});

  const refreshProgressMapFromStorage = useCallback(() => {
    const map = {};
    const vids = categoryVideos[0]?.videos || [];
    vids.forEach(v => {
      const p = loadProgress(v.slug);
      if (p) map[v.slug] = p;
    });
    setProgressMap(map);
  }, [categoryVideos]);

  const upsertHistory = useCallback((meta) => {
    const arr = loadHistoryIndex();
    const idx = arr.findIndex(e => e.slug === meta.slug);
    const entry = {
      slug: meta.slug, title: meta.title || "", thumbnail: meta.thumbnail || "",
      categoryTitle: meta.categoryTitle || "",
      seconds: Math.max(0, Math.floor(meta.seconds || 0)),
      duration: Math.max(0, Math.floor(meta.duration || 0)),
      percent: meta.duration ? Math.min(100, Math.round((meta.seconds / meta.duration) * 100)) : 0,
      updatedAt: Date.now(),
    };
    if (idx >= 0) arr.splice(idx, 1);
    arr.unshift(entry);
    saveHistoryIndex(arr);
  }, []);

  const handleVimeoReady = async (player) => {
    vimeoPlayerRef.current = player;
    try {
      const duration = await player.getDuration();
      const saved = loadProgress(slug);
      if (saved && saved.seconds && duration) {
        const safeTarget = Math.max(0, Math.min(saved.seconds, duration - 2));
        if (safeTarget > 0) await player.setCurrentTime(safeTarget);
      }
    } catch {}
  };

  const handleVimeoTimeUpdate = async (data) => {
    const current = Math.floor(data.seconds || 0);
    if (current < 0) return;
    if (current !== lastSavedSecondRef.current && current % 5 === 0) {
      lastSavedSecondRef.current = current;
      try {
        const duration = vimeoPlayerRef.current ? await vimeoPlayerRef.current.getDuration() : (video?.duration || 0);
        saveProgress(slug, current, duration || 0);
        setProgressMap(prev => ({
          ...prev,
          [slug]: { seconds: current, duration: Math.max(0, Math.floor(duration || 0)), updatedAt: Date.now() }
        }));
        upsertHistory({
          slug,
          title: video?.title,
          thumbnail: video?.thumbnail,
          categoryTitle: (categoryVideos[0]?.title) || (video?.Category?.title) || "",
          seconds: current,
          duration: duration || 0,
        });
      } catch {}
    }
  };

  const handleVimeoEnd = async () => {
    try {
      const duration = vimeoPlayerRef.current ? await vimeoPlayerRef.current.getDuration() : (video?.duration || 0);
      saveProgress(slug, duration || 0, duration || 0);
      setProgressMap(prev => ({ ...prev, [slug]: { seconds: duration || 0, duration: duration || 0, updatedAt: Date.now() } }));
      upsertHistory({
        slug,
        title: video?.title,
        thumbnail: video?.thumbnail,
        categoryTitle: (categoryVideos[0]?.title) || (video?.Category?.title) || "",
        seconds: duration || 0,
        duration: duration || 0,
      });
    } catch {}
  };

  return {
    vimeoPlayerRef,
    lastSavedSecondRef,
    progressMap,
    refreshProgressMapFromStorage,
    handleVimeoReady,
    handleVimeoTimeUpdate,
    handleVimeoEnd
  };
}